import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { getJourneyState } from "@/lib/journey.functions";
import { Card } from "@/components/ui/card";
import { Trophy } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/journey/achievements")({
  component: AchievementsPage,
});

interface Badge { id: string; title: string; description: string | null; icon: string | null }

function AchievementsPage() {
  const load = useServerFn(getJourneyState);
  const [state, setState] = useState<Awaited<ReturnType<typeof getJourneyState>> | null>(null);
  const [badges, setBadges] = useState<Badge[]>([]);

  useEffect(() => {
    load().then(setState).catch(() => setState(null));
    supabase.from("badges").select("id, title, description, icon").then(({ data }) => setBadges((data ?? []) as Badge[]));
  }, [load]);

  const earned = new Set(state?.badges.map((b) => b.badge_id));
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {badges.map((b) => {
        const has = earned.has(b.id);
        return (
          <Card key={b.id} className={`p-4 ${has ? "border-gold/60" : "opacity-60"}`}>
            <div className="flex items-center gap-3">
              <div className={`flex h-10 w-10 items-center justify-center rounded-full ${has ? "bg-gold/20 text-gold" : "bg-foreground/10 text-foreground/50"}`}>
                <Trophy size={18} />
              </div>
              <div>
                <div className="font-serif text-base text-gold-luminous">{b.title}</div>
                {b.description && <div className="text-xs text-foreground/60">{b.description}</div>}
              </div>
            </div>
            <div className="mt-2 text-xs text-foreground/50">{has ? "Earned" : "Locked"}</div>
          </Card>
        );
      })}
      {badges.length === 0 && <p className="text-sm text-foreground/60">Achievements will appear here as you practice.</p>}
    </div>
  );
}
